/**
 * Optimizely feature flags.
 *
 * Adding a member here is NOT enough to make a flag work. The server only asks
 * Optimizely about the keys listed in `OPTIMIZELY_FEATURE_FLAGS` in
 * `constants/features/*`, so an unregistered flag never reaches the client and
 * `useFeatureFlag` reads `false` in every environment, however it is configured
 * in Optimizely. Register the key there as well.
 */
export enum OptimizelyFeatureFlags {
  showDreamBlogLinks = 'show_dream_blog_links',
  luxPlusToursToggle = 'luxplus_tours_toggle',
  luxPlusDiscountedInsuranceBenefit = 'lux_plus_discounted_insurance_benefit',
  luxPlusDiscountedBookingProtectionBenefit = 'lux_plus_discounted_booking_protection_benefit',
  luxPlusLppPricingEnabled = 'luxplus_lpp_pricing_enabled',
  luxPlusCruiseInclusionsEnabled = 'lux_plus_cruise_inclusions',
  luxPlusDiscountedCruise = 'lux_plus_discounted_cruise',
  luxPlusDiscountedExperiencesBenefit = 'lux_plus_discounted_experiences_benefit',
  allowPhoneNumberLogin = 'allow_phone_number_login',
  promoAppBanner = 'app_banner_targeted_delivery',
  CSAgentsAutoQuote = 'cro_cs_agents_auto_quote',
  toursRequestAgentCallbackEnabled = 'csx_tours_request_agent_callback',
  hotelsAndVillasRequestAgentCallbackEnabled = 'csx_hotels_and_villas_request_agent_callback',
  upsellTilesFeatureToggle = 'cro_upsell_on_homepage_and_my_escapes',
  croTravelPreferencesModalEnabled = 'cro_travel_preferences_modal',
  opexTermsAndConditionsModal = 'terms_and_conditions_modal',
  croWebPushNotificationPrompt = 'cro_web_push_notification_prompt',
  opexFraudOrdersOtp = 'opex_fraud_orders_otp',
  payIdTargetedRelease = 'pay_id_targeted_release',
  carHireBundledDiscount = 'car_hire_bundled_discount',
  lereDestinationModelEnabled = 'lere_personalised_destinations',
  lereOfferPageRecommendationsEnabled = 'lere_offer_page',
  lereHeroCarouselEnabled = 'lere_hero_carousel',
  opexHotelAddExtraGuestsEnabled = 'hotel_add_extra_guests',
  opexLPCAddExtraGuestsEnabled = 'lpc_add_extra_guests',
  flightsEnableSabreContent = 'flights_enable_sabre_content',
  flightsLuxPlusPricing = 'flights_lux_plus_pricing',
  flightsBundledLuxPlusPricing = 'flights_bundled_lux_plus_pricing',
  flightsBundleLuxPlusPriceWithSubscriptionInCart = 'flights_bundle_lux_plus_price_with_subscription_in_cart',
  flightsLuxPlusPreview = 'flights_lux_plus_preview',
  flightsShowLuxPlusPriceToEveryone = 'flights_show_luxplus_price_to_everyone',
  flightsCombinedTileV2 = 'flights_combined_tile_v2',
  flightsSingleLegRedesign = 'flights_single_leg_redesign',
  flightsInlineUpsell = 'flights_inline_upsell',
  loyaltyLuxPlusRenewalReward = 'loyalty_lux_plus_renewal_reward',
  loyaltyPointsDeductionWarning = 'loyal_points_deduction_warning',
  croInsuranceXCoverEnabled = 'cro_insurance_xcover_enabled',
  croPayAtProperty = 'cro_pay_at_property',
  croInclusionValidity = 'cro_inclusion_validity',
  croPackageUpgradeLibEnabled = 'cro_package_upgrade_lib_enabled',
  standaloneFlightLoungePassExperiencesEnabled = 'standalone_flight_lounge_pass_experiences_enabled',
  merchandiseLaunch = 'merchandise_launch',
  loyaltyPurchasePointsEnabled = 'loyalty_points_purchase',
  opexBookingAuthority = 'opex_booking_authority',
  opexLESAConditionalBubble = 'opex_lesa_conditional_bubble',
  opexBookingAuthorityManageAccess = 'opex_booking_authority_manage_access',
  luxRow = 'lux_row',
  maldivesCharterRewardSeats = 'maldives_charter_reward_seats',
  croWrappedModalTravelled = 'cro_wrapped_modal',
  croWrappedModalAll = 'cro_wrapped_modal_all',
  croDisplaySimilarAccounts = 'cro_display_similar_accounts',
  randomizeExperiencesOffers = 'randomize_experiences_offers',
  flightsDateGrid = 'flights_date_grid',
  flightsDateGridStandalone = 'flights_date_grid_standalone',
  flightsDateGridBar = 'flights_date_grid_bar',
  disallowPartialFlightsSeatSelection = 'disallow_partial_flights_seat_selection',
  loyaltyLuxplusLandingPage = 'loyalty_luxplus_landing_page',
  childrenFlyFree = 'children_fly_free',
  flightsPriceAlerts = 'flights_price_alerts',
  /** FLY-5770 kill switch for interleaving campaign banners and price alerts between results. */
  flightsResultsInsertsEnabled = 'flights_results_inserts_enabled',
  croPassportCardExclusiveCheckout = 'cro_passport_card_exclusive_checkout',
  loyaltyEsimBenefit = 'loyalty_esim_benefit',
  searchTypeaheadGeoTypeIndexed = 'search_typeahead_geo_type_indexed',
  searchSalesCtrRanking = 'search_sales_ctr_ranking',
  searchSalesMpvRanking = 'search_sales_mpv_ranking',
  croOnlyInAustraliaCarousel = 'cro_only_in_australia_carousel',
  flightsFareSense = 'flights_fare_sense',
  opexBookAgain = 'opex_book_again',
  opexSelfServiceNameChange = 'opex_self_service_name_change',
  aiOpsSpoofLaunchPadWhitelist = 'ai-ops-spoof-launch-pad-whitelist',
  leLivePopup = 'le_live_popup',
  experiencesSearchAttractions = 'experiences_search_attractions',
  maldivesCharterFnfPage = 'maldives_charter_fnf_page',
  maldivesCharterReturnFlightFree = 'maldives_charter_return_flight_free',
  charterLaunchOffer100 = 'charter_launch_offer_100',
  noPlaceLikeHomeCampaign = 'no_place_like_home_campaign',
  loyaltySppPackageUpgrades = 'loyalty_spp_package_upgrades',
  sppMultiIndividualRoom = 'spp_multi_individual_room',
  hotelPropertyTypeFilter = 'hotel_property_type_filter',
  flightsSelfCancellation = 'flights_self_cancellation',
  flightsSelfDateChange = 'flights_self_date_change',
  myEscapesFlightEticketResend = 'my_escapes_flight_eticket_resend',
  flightsBaggagesAddonCheckout = 'flights_baggages_addon_checkout',
  flightsBaggagesAddonPostPurchase = 'flights_baggages_addon_post_purchase',
  croLocalDestinationOfferCarousel = 'cro_local_destination_offer_carousel',
  bnblV2 = 'voice_ai_bnbl_v2',
}
